import React from 'react';
import { connect } from 'react-redux';
import { IRootState } from '../../../@types/redux';
import { withRouter, RouteComponentProps } from 'react-router';
import Paper from '@material-ui/core/Paper';
import { createSelector } from 'reselect';
import { CROP } from '../../../appConstants';
import { PHENOLOGY_ASSIGNMENT_TYPE, IPhenologyAssignment } from '../../../types/model/phenology/assignment';
import moment from 'moment';
import PhenologyFunctions from '../../../store/scouting/functions';
import { Transitions } from '../../customComponents/animations/Transitions';
import NavFunctions from '../../../store/nav/functions';
import { IAssignmentBlock } from '../../../types/model/masterData/block';
import PhenologyAssignmentCreateDialog from '../dialog/AssignmentCreateDialog';
import { IDivision } from '../../../types/model/rights/division';
import { MapBoxRef } from '../../customComponents/mapBox/MapBox';
import { CircularProgress } from '@material-ui/core';
import BasicMap from '../../customComponents/mapBox/BasicMap';
import PhenologyAssignmentFieldSelector from './AssignmentFieldSelector';
import MapboxPointLayer from '../../customComponents/mapBox/layer/masterData/PointLayer';

interface IPhenologyAssignmentCreateProps extends RouteComponentProps {
    isLoading : boolean;

    assignments : Array<IPhenologyAssignment>;
}

interface IPhenologyAssignmentCreateState {
    type : PHENOLOGY_ASSIGNMENT_TYPE | null;

    selectedPhenologyBlocks : Record<string, Array<IAssignmentBlock>>;

    date : moment.Moment;
    division ?: IDivision;

    showCreate : boolean;
}

class PhenologyAssignmentCreateComponent extends React.Component<IPhenologyAssignmentCreateProps, IPhenologyAssignmentCreateState> {
    private mapRef : MapBoxRef | null = null;

    constructor(props : IPhenologyAssignmentCreateProps) {
        super(props);
        this.state = {
            selectedPhenologyBlocks: {},
            date: moment.utc().startOf('day'),
            showCreate: false,
            type: null,
        };
    }

    public readonly componentDidMount = () => {
        PhenologyFunctions.getAssignments(this.getCrop(this.props), false);
    };

    private readonly getSearch = (props : IPhenologyAssignmentCreateProps) => props.location.search;
    private readonly getCrop = createSelector([
        this.getSearch,
    ], (search) => {
        const urlParams = new URLSearchParams(search);
        const crop = urlParams.get('crop');
        return crop as CROP;
    });

    private readonly setMapRef = (mapRef : MapBoxRef) => {
        this.mapRef = mapRef;
    };
    private readonly onWeekChanged = (date ?: moment.Moment) => {
        if (!date) return;

        this.setState({
            date,
        });
    };

    private readonly onDivisionChanged = (division ?: IDivision) => {
        this.setState({
            division,
            selectedPhenologyBlocks: {},
        });
    };

    private readonly onTypeChanged = (type ?: PHENOLOGY_ASSIGNMENT_TYPE) => {
        this.setState({
            type: type ?? null,
        });
    };

    private readonly onFieldsChange = (selectedPhenologyBlocks : Record<string, Array<IAssignmentBlock>>) => {
        this.setState({
            selectedPhenologyBlocks,
        });
    };

    private readonly onAssignmentCreateClose = (result : boolean) => {
        this.setState({
            showCreate: !this.state.showCreate,
        });

        if (result) {
            NavFunctions.navReplacePath('/phenology/assignments', this.props.location.search);
        }
    };

    private readonly onAssignmentAddClick = () => {
        this.setState({
            showCreate: !this.state.showCreate,
        });
    };

    public render = () => {
        const { isLoading } = this.props;
        const {
            division,
            date,
            type,
            selectedPhenologyBlocks,
            showCreate,
        } = this.state;

        const crop = this.getCrop(this.props);

        return (
            <div className={'fdc hfill p10 mh0 mw0 bcg0'}>
                {
                    isLoading &&
                    <div className='fdc flx1 aic jcc'>
                        <CircularProgress />
                    </div>
                }
                {
                    !isLoading &&
                    <div className='fdr hfill'>
                        <Paper className={'fdc flx3 p5 m5'}>
                            <BasicMap
                                id={'scoutingMap'}
                                onMap={this.setMapRef}
                            >
                                <MapboxPointLayer
                                    pointsId='point_farm_layer'
                                    minZoom={15}
                                    type='phenology'
                                    crop={crop}
                                />
                            </BasicMap>
                        </Paper>
                        <PhenologyAssignmentFieldSelector
                            mapRef={this.mapRef}
                            crop={crop}
                            selectedPhenologyBlocks={selectedPhenologyBlocks}
                            date={date}
                            onChange={this.onFieldsChange}
                            onWeekChanged={this.onWeekChanged}
                            division={division}
                            onDivisionChanged={this.onDivisionChanged}
                            phenologyType={type}
                            onPhenologyTypeChange={this.onTypeChanged}
                            onAssignmentAddClick={this.onAssignmentAddClick}
                        />
                    </div>
                }
                <PhenologyAssignmentCreateDialog
                    open={showCreate}
                    isLoading={isLoading}
                    date={date}
                    division={division?.code}
                    transition={Transitions.Down}
                    maxWidth='md'
                    fullWidth
                    onClose={this.onAssignmentCreateClose}
                    blocks={selectedPhenologyBlocks}
                    type={type}
                    crop={crop}
                />
            </div>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        isLoading: state.phenology.general.isLoadingAssignments,
    };
};

const PhenologyAssignmentCreate = connect(
    mapStateToProps,
)(withRouter(PhenologyAssignmentCreateComponent));

export default PhenologyAssignmentCreate;
